// Atomic group removal: ONE batched /cart/update.js call zeroing every line
// in the group (quick-order-list.js precedent), announced through the same
// CartLinesUpdateEvent discipline core uses. Core cart-items-component owns
// the section morph (its #handleCartUpdate awaits the event promise and
// morphs detail.sections[sectionId], falling back to a Section Rendering
// API refetch when sections are absent), and cart-icon.js reads
// cart.totalQuantity from the same promise to update the header bubble.
import { CartLinesUpdateEvent } from '@shopify/events';

class EgCartGroups {
  constructor() {
    document.addEventListener('click', this.onClick);
  }

  onClick = async (e) => {
    const btn = e.target.closest('[data-eg-remove-group]');
    if (!btn || btn.disabled) return;
    const container = btn.closest('cart-items-component');
    const sectionId = container?.dataset.sectionId;
    const note = btn.closest('.eg-cart-group')?.querySelector('[data-eg-group-error]');
    btn.disabled = true;
    container?.setAttribute('aria-busy', 'true');
    if (note) note.hidden = true;
    let deferred = null;
    try {
      const keys = JSON.parse(btn.dataset.egLines || '[]');
      if (!Array.isArray(keys) || !keys.length) throw new Error('No group lines to remove');

      // Dispatch before the fetch with a pending promise, exactly as core
      // dispatchers do (component-cart-items.js updateQuantity,
      // quick-order-list.js #removeAllItems). Dispatched on the button so
      // it bubbles to document without event.target being the
      // cart-items-component itself (its #handleCartUpdate ignores those).
      deferred = CartLinesUpdateEvent.createPromise();
      btn.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'remove',
          context: 'cart',
          lines: keys.map((key) => ({ id: key, quantity: 0 })),
          promise: deferred.promise,
        })
      );

      const body = {
        updates: Object.fromEntries(keys.map((k) => [k, 0])),
        // sections_url mirrors core discipline (component-cart-items.js:228,
        // quick-order-list.js:259-265) — template-scoped section ids only
        // resolve in their own template's URL context.
        sections: sectionId ? [sectionId] : undefined,
        sections_url: window.location.pathname,
      };
      const updateRoute = window.Theme?.routes?.cart_update_url || '/cart/update';
      const res = await fetch(updateRoute.endsWith('.js') ? updateRoute : `${updateRoute}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`cart update ${res.status}`);
      const cart = await res.json();
      if (cart.errors) {
        throw new Error(typeof cart.errors === 'string' ? cart.errors : JSON.stringify(cart.errors));
      }

      // Same resolution shape core consumers destructure ({ cart, detail }):
      // component-cart-items.js:271-280, quick-order-list.js:290-299.
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'eg-cart-groups',
          didError: false,
        },
      });
    } catch (err) {
      // Reject only if the event went out — otherwise nothing holds the
      // promise and rejecting it would surface as an unhandled rejection.
      deferred?.reject(err);
      if (note) note.hidden = false;
    } finally {
      // On success the morph replaces this DOM anyway; re-enabling here
      // guarantees no path leaves the button disabled or the section
      // marked busy.
      btn.disabled = false;
      container?.removeAttribute('aria-busy');
    }
  };
}

if (!window.__egCartGroups) window.__egCartGroups = new EgCartGroups();
